import React from 'react';
import PropTypes from 'prop-types';
import { getToolIds } from '../../util/tool';
import { getConfigObject } from '../../util/config';
import { getPathfinderUrl } from '../../util/util';
import { REPORT_TYPES } from '../../actions/constants';
import { StudentActivityContainer } from '../../StudentActivity/StudentActivity';
import { StudentCourseTotalContainer } from '../../StudentCourseTotal/StudentCourseTotal';
import DefaultOverviewWidget from './OverviewWidgetComponents/DefaultOverviewWidget';
import QQOverviewWidget from './OverviewWidgetComponents/QQOverviewWidget';
import PathFinderDiagnosticsWidget from './OverviewWidgetComponents/PathFinderDiagnosticsWidget';
import s from './overviewWidget.module.css';

const checkForActiveUnit = (data) => (data && !!data.activeUnit) || false;

/**
 * Component that renders the appropriate widgets for Quant/Qual courses.
 * Responsible for component null states.
 *
 * - Renders a dynamic widget if there is an active unit and widgets are enabled.
 * - Renders a pathfinder widget in place of the dynamic widget if there is no
 * active unit and is a pathfinder assignment.
 * - Renders a static widget if the dynamic widgets are disabled
 *
 * @param {*} props
 * @returns
 */
const QQWidgetsContainer = (props) => {
  const {
    reports,
    reports: {
      overviewWidget,
      courseAbstract,
      courseAbstract: { courseShortId, isStudent } = {},
    },
    onTabChange,
    loading,
    error,
    isSectionManager,
    getLaunchUrl,
    shouldDisplayStudentActivity,
  } = props;

  const {
    DASHBOARDS_ENABLED_QQ,
    DASHBOARD_ENABLED_INSIGHTS_SNAPSHOT_WIDGET,
    DASHBOARDS_ENABLED_110,
    FF_ENABLE_STUDENT_COURSE_TOTAL,
  } = getConfigObject();

  if (
    !DASHBOARDS_ENABLED_QQ ||
    isStudent ||
    error ||
    (!loading && !reports.authorized) ||
    isSectionManager
  ) {
    return null;
  }

  const handleISGLaunch = (studentId) => {
    if (!studentId) return;
    const performanceUrl = `performance?studentId=${studentId}`;
    onTabChange(performanceUrl);
  };

  if (!DASHBOARD_ENABLED_INSIGHTS_SNAPSHOT_WIDGET || reports.comingSoon) {
    const {
      learningcurve: learningcurveToolId,
      assessment: assessmentToolId,
    } = getToolIds();
    const performanceTab =
      DASHBOARDS_ENABLED_110 || reports.comingSoon
        ? 'reports'
        : `reports/type/${REPORT_TYPES.subunit}`;
    const hasQQAssignments = reports.courseAbstract.toolIds.includes(
      learningcurveToolId || assessmentToolId
    );
    return (
      hasQQAssignments && (
        <div className={s.overviewWidget}>
          {shouldDisplayStudentActivity && (
            <StudentActivityContainer
              courseId={courseAbstract.id}
              courseShortName={courseAbstract.shortId}
              handleISGLaunch={handleISGLaunch}
              apiHost={getConfigObject()?.DASHBOARD_API_URL}
            />
          )}
          {shouldDisplayStudentActivity &&
            FF_ENABLE_STUDENT_COURSE_TOTAL && (
              <StudentCourseTotalContainer
                courseId={courseAbstract.id}
                handleISGLaunch={handleISGLaunch}
                apiHost={getConfigObject()?.DASHBOARD_API_URL}
              />
            )}
          <DefaultOverviewWidget
            performanceTab={performanceTab}
            diagnosticsUrl={getPathfinderUrl(courseAbstract.id)}
            btnText="View Unit Performance"
            widgetTitle="Students Insights"
            widgetDescription="A snapshot of student performance in the most recent unit."
            onTabChange={onTabChange}
          />
        </div>
      )
    );
  }

  const hasActiveUnit = checkForActiveUnit(overviewWidget);
  const shouldDisplayQQOverviewWidget =
    (hasActiveUnit && overviewWidget && overviewWidget.hasStudents) || loading;
  const shouldDisplayQQDiagnosisCard =
    !hasActiveUnit &&
    overviewWidget &&
    overviewWidget.pathfinderUnit &&
    overviewWidget.hasStudents;

  if (
    !shouldDisplayQQOverviewWidget &&
    !shouldDisplayQQDiagnosisCard &&
    !shouldDisplayStudentActivity
  ) {
    return null;
  }

  return (
    <div className={s.overviewWidget}>
      <div className={`${s.OverviewCard} ${s.QQOverviewCard}`}>
        {shouldDisplayStudentActivity && (
          <div>
            <div className={s.OverviewSection}>
              <div className={s.viewInsightsHeader}>Course Insights</div>
            </div>
            <StudentActivityContainer
              courseId={courseAbstract.id}
              courseShortName={courseAbstract.shortId}
              handleISGLaunch={handleISGLaunch}
              apiHost={getConfigObject()?.DASHBOARD_API_URL}
            />
            {FF_ENABLE_STUDENT_COURSE_TOTAL && (
              <StudentCourseTotalContainer
                courseId={courseAbstract.id}
                handleISGLaunch={handleISGLaunch}
                apiHost={getConfigObject()?.DASHBOARD_API_URL}
              />
            )}
          </div>
        )}
        {shouldDisplayQQOverviewWidget && (
          <QQOverviewWidget
            onTabChange={onTabChange}
            loading={loading}
            data={overviewWidget}
            shortId={courseShortId}
            getLaunchUrl={getLaunchUrl}
            reports={reports}
          />
        )}
      </div>
      {shouldDisplayQQDiagnosisCard && (
        <PathFinderDiagnosticsWidget
          diagnosticsUrl={getPathfinderUrl(courseAbstract.id)}
        />
      )}
    </div>
  );
};
QQWidgetsContainer.propTypes = {
  reports: PropTypes.object,
  onTabChange: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.any,
  isSectionManager: PropTypes.bool,
  shouldDisplayStudentActivity: PropTypes.bool,
};

export default QQWidgetsContainer;
